/* eslint-disable react-hooks/immutability -- this dropdown button mutates the existing option object state by design. */
import type {MouseEvent, ReactElement} from "react";
import {cloneElement, useCallback, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import type {TransformObj} from "../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {useAppStore} from "../../../StateManager";
import {useClickOutside, useCloseOnKeyboardPress} from "../utils";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {colorPalette} from "../styles/colorPalette";
import {FLEXCENTER, radius, zIndex} from "../styles/styles";
import {ReactUtils} from "../../utils/ReactUtils";
import {Functions} from "../../../utils/function/Functions";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {DropdownOptionsV5} from "./DropdownOptionsV5";
import type {IDropdownOption, ISubOptionWrapper} from "./DropdownOptionsV5";
import {IconButtonStyled} from "./IconButtonV5";

interface IDropdownButtonProps {
	readonly button: ReactElement;
	readonly options: (IDropdownOption | ISubOptionWrapper)[];
	readonly className?: string;
	readonly horizontalAlignment?: HorizontalAlignment;
	readonly verticalAlignment?: VerticalAlignment;
	readonly offsetX?: number;
	readonly offsetY?: number;
	readonly optionsZIndex?: number;
	readonly width?: string;
	readonly changeAlignmentOnPositionCorrection?: boolean;
	readonly style?: React.CSSProperties;
	readonly parentStyle?: React.CSSProperties;
	readonly showSearch?: boolean;
	readonly closeOnMouseLeave?: boolean;
	readonly isPortfolioSwitch?: boolean;
	readonly onlyIcons?: boolean;
	readonly title?: string;
	readonly activeOption?: (IDropdownOption | ISubOptionWrapper)[];
	readonly noPortal?: boolean;
	readonly onHover?: (e?: React.MouseEvent) => void;
	readonly onOpenChange?: (isOpen: boolean) => void;
	readonly dataCy?: string;
}

export const DropdownButtonV5 = ({
	button,
	options,
	className = "",
	horizontalAlignment = HorizontalAlignment.center,
	verticalAlignment = VerticalAlignment.bottomOuter,
	offsetX = 0,
	offsetY = 8,
	width = "inherit",
	optionsZIndex = zIndex.dropdowns,
	changeAlignmentOnPositionCorrection = true,
	showSearch = false,
	style,
	parentStyle = {},
	closeOnMouseLeave = false,
	onlyIcons = false,
	title = "",
	activeOption,
	isPortfolioSwitch,
	noPortal = false,
	onHover = Functions.emptyFunction,
	onOpenChange = Functions.emptyFunction,
	dataCy,
}: IDropdownButtonProps) => {
	const appState = useAppStore((state) => state.appState);
	const modalContainer = appState.app.modalContainer;
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [optionsTransform, setOptionsTransform] = useState<TransformObj | null>(null);
	const _container = useRef<HTMLDivElement>(null);
	const _floatingElement = useRef<HTMLDivElement>(null);

	const onToggle = (event: MouseEvent) => {
		event.stopPropagation();

		const newValue = !isOpen;

		setIsOpen(newValue);
		onOpenChange?.(newValue);

		if (!newValue) {
			setOptionsTransform(null);
		}
	};

	const onClose = useCallback(() => {
		setIsOpen(false);
		onOpenChange?.(false);
		setOptionsTransform(null);
	}, [onOpenChange]);

	useClickOutside([_container, _floatingElement], onClose, "mousedown", [".DropdownOptions"], true);
	useCloseOnKeyboardPress(onClose, KeyboardListener.KEY_ESCAPE, [isOpen], {
		enabled: isOpen,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	useEffect(() => {
		if (isOpen && _container.current && _floatingElement.current) {
			setOptionsTransform(
				DomUtils.getFixedFloatingElementPosition(
					_container.current,
					_floatingElement.current,
					verticalAlignment,
					horizontalAlignment,
					offsetY,
					offsetX,
					changeAlignmentOnPositionCorrection,
				),
			);
		}
	}, [isOpen, horizontalAlignment, verticalAlignment, offsetX, offsetY, changeAlignmentOnPositionCorrection]);

	const buttonClone = cloneElement(button);

	const inlineStyle: React.CSSProperties = noPortal
		? style
		: {
				transform: optionsTransform?.translate,
				visibility: optionsTransform ? "visible" : "hidden",
				width,
				...style,
			};

	if (onlyIcons) {
		inlineStyle.minWidth = "auto";
	}

	return (
		<DropdownButtonStyled
			ref={_container}
			className={ReactUtils.cls(className || "", {isOpen})}
			onClick={onToggle}
			style={parentStyle}
			onMouseOver={onHover}
			title={title}
			onMouseLeave={() => isOpen && _container?.current.classList.add("hover")}
			data-cy={dataCy}
		>
			{buttonClone}
			{isOpen && options && (
				<DomPortal
					destination={modalContainer}
					noPortal={noPortal}
				>
					<DropdownOptionsV5
						className={`${className}_options`}
						divRef={_floatingElement}
						style={inlineStyle}
						options={options}
						onClose={onClose}
						optionsZIndex={optionsZIndex}
						showSearch={showSearch}
						closeOnMouseLeave={closeOnMouseLeave}
						onlyIcons={onlyIcons}
						activeOption={activeOption}
						isPortfolioSwitch={isPortfolioSwitch}
					/>
				</DomPortal>
			)}
		</DropdownButtonStyled>
	);
};

export const DropdownButtonStyled = styled.div`
	${FLEXCENTER};
	cursor: pointer;
	height: 32px;
	border-radius: ${radius.sm};
	position: relative;

	&:hover,
	&.hover {
		background-color: ${colorPalette.gray.c200Light};

		${IconButtonStyled} {
			background-color: initial;
		}
	}

	&.isOpen {
		&.portfolioSelector {
			background-color: ${colorPalette.primary.c500Primary};
		}
	}

	&.borderButton {
		color: ${colorPalette.primary.c500Primary};
		border: 1px solid ${colorPalette.primary.c500Primary};
	}
`;
